<script lang="ts">
    import type {IssueAwareVolume, KpVolumesComponentCapabilities} from './types';
    import type {Document, LabeledIdentified} from 'typings/portaro.be.types';
    import type {KpVolumesPresenter} from './kp-volumes.presenter';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {Disclosure, DisclosureButton, DisclosurePanel} from '@rgossiaux/svelte-headlessui';
    import {getLocalization} from 'core/svelte-context/context';
    import {slide} from 'svelte/transition';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import KpExemplarsList from '../kp-exemplars-list/KpExemplarsList.svelte';
    import {createEventDispatcher} from 'svelte';
    import {fade} from 'svelte/transition';
    import {heightResizeAnimation} from 'shared/svelte-actions/use.height-resize-animation';
    import {exists} from 'shared/utils/custom-utils';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import {volumesAccordionSlideAnimationDurationMs} from './defaults';

    export let document: Document;
    export let volume: IssueAwareVolume;
    export let exemplarsListProperties: LabeledIdentified<string>[];
    export let capabilities: KpVolumesComponentCapabilities;
    export let presenter: KpVolumesPresenter;

    const localize = getLocalization();
    const dispatch = createEventDispatcher<{ 'first-open': void }>();
    const panelSlideAnimParams = {duration: volumesAccordionSlideAnimationDurationMs};

    let loadInitialized = false;

    const handleOpenClick = () => {
        if (loadInitialized) {
            return;
        }

        dispatch('first-open');
        loadInitialized = true;
    }
</script>

<Disclosure as="li"
            class="kp-volume-accordion-container"
            let:open>

    <DisclosureButton on:click={handleOpenClick} class="kp-volume-accordion-button {open ? 'opened' : ''}">
        <div class="arrow-container" class:opened={open}>
            <UIcon icon="angle-small-down"/>
        </div>

        <span class="year-text" class:text-muted={!volume.withIssues}>
            {localize(/* @kp-localization commons.rocnik */ 'commons.rocnik')} {pipe(volume, loc())}
            ({localize(/* @kp-localization commons.rok */ 'commons.rok')} {volume.year})
        </span>

        {#if capabilities.canEditAnyVolume}
            <small>{localize(/* @kp-localization volume.pocetKusu */ 'volume.pocetKusu')}: {volume.issueQuantity}</small>
            {#if volume.description}
                <small>{localize(/* @kp-localization volume.poznamka */ 'volume.poznamka')}: {volume.description}</small>
            {/if}
        {/if}
    </DisclosureButton>

    {#if open}
        <div transition:slide={panelSlideAnimParams}>
            <DisclosurePanel class="kp-volume-accordion-panel panel panel-default" static>
                <div use:heightResizeAnimation>
                    {#if exists(volume.issues)}
                        <div in:fade>
                            <KpExemplarsList document="{document}"
                                             properties="{exemplarsListProperties}"
                                             exemplars="{volume.issues}"/>
                        </div>
                    {:else}
                        <div class="loading-container">
                            <KpLoadingBlock size="sm"/>
                        </div>
                    {/if}

                    {#if capabilities.canEditAnyVolume || capabilities.canCreateAnyExemplar}
                        <div class="action-buttons-container">
                            <KpButton on:click={() => presenter.deleteVolume(volume)}>
                                <IconedContent icon="trash">
                                    {localize(/* @kp-localization exemplar.DeleteVolume */ 'exemplar.DeleteVolume')}
                                </IconedContent>
                            </KpButton>

                            <KpButton on:click={() => presenter.showVolumeEditation(volume, document)}>
                                <IconedContent icon="edit">
                                    {localize(/* @kp-localization exemplar.EditVolume */ 'exemplar.EditVolume')}
                                </IconedContent>
                            </KpButton>

                            <KpButton on:click={() => presenter.createIssue(document, volume)}>
                                <IconedContent icon="add">
                                    {localize(/* @kp-localization commons.pridatExemplar */ 'commons.pridatExemplar')}
                                </IconedContent>
                            </KpButton>
                        </div>
                    {/if}
                </div>
            </DisclosurePanel>
        </div>
    {/if}
</Disclosure>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    :global {
        .kp-volume-accordion-button {
            width: 100%;
            outline: none;
            border: 1px solid @table-border-color;
            display: flex;
            align-items: center;
            justify-content: start;
            padding: @panel-heading-padding;
            background-color: @panel-default-heading-bg;
            border-radius: @panel-border-radius;
            gap: @padding-small-horizontal;
            transition: border-color 0.2s ease-in-out;

            &:hover {
                border-color: var(--accent-blue-new);
                color: var(--accent-blue-new);

                .text-muted {
                    color: inherit;
                }
            }

            &.opened {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }
        }

        .kp-volume-accordion-panel.panel {
            border-top-left-radius: 0;
            border-top-right-radius: 0;
            margin-bottom: 0;
            border-top: none;
        }
    }

    .arrow-container {
        transition: transform 0.2s ease-in-out;

        &.opened {
            transform: rotate(-180deg) translateY(1px);
        }
    }

    .year-text {
        margin-right: auto;
    }

    .loading-container {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 25px @panel-body-padding;
    }

    .action-buttons-container {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: @padding-base-horizontal;
        padding: @panel-body-padding;
        border-top: 1px solid @panel-default-border;
    }
</style>